import type {
  ClientFailureRecord,
  ClientFailuresSummaryResponse,
  PluginFailureRecord,
  PluginFailuresSummaryResponse,
} from "@voice2mail/shared/v2m_api";

import { MetricCard } from "./MetricCard";

interface ProductionFailuresProps {
  pluginSummary: PluginFailuresSummaryResponse | null;
  pluginRecent: PluginFailureRecord[];
  clientSummary: ClientFailuresSummaryResponse | null;
  clientRecent: ClientFailureRecord[];
}

function fmtTime(d: Date | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

function BreakdownList({ byKey }: { byKey: Record<string, number> }) {
  const entries = Object.entries(byKey).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    return <p className="text-sm text-slate-500">Brak awarii dzisiaj.</p>;
  }
  return (
    <ul className="text-sm text-slate-700 space-y-1">
      {entries.map(([key, count]) => (
        <li key={key} className="flex justify-between">
          <span className="font-mono">{key}</span>
          <span className="font-semibold">{count}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Read view for `plugin_failure_reports`/`client_failure_reports` (V2M-DEV-010).
 *
 * Before this component, both channels already paged PK's phone via ntfy in
 * real time (checkpoint 2026-07-25-v2m-telemetria-nie-widzimy-produkcji.md,
 * SEDNO point 6: "Najbogatszy materiał diagnostyczny jest WRITE-ONLY") — the
 * only way to review history was an ephemeral push. The backend read
 * endpoints (`src/api/admin/plugin_failures.py`, `client_failures.py`)
 * already existed; this panel is the missing consumer.
 */
export function ProductionFailures({
  pluginSummary,
  pluginRecent,
  clientSummary,
  clientRecent,
}: ProductionFailuresProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-4">
        <h4 className="text-sm font-semibold text-slate-700">
          Plugin failures ({pluginSummary?.date ?? "—"})
        </h4>
        <MetricCard
          label="Today"
          value={pluginSummary?.totalToday ?? 0}
          warn={(pluginSummary?.totalToday ?? 0) > 0}
        />
        {pluginSummary && <BreakdownList byKey={pluginSummary.byStage} />}
        {pluginRecent.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <h5 className="text-xs font-medium uppercase text-slate-500 mb-2">Recent</h5>
            <ul className="text-xs text-slate-600 space-y-1 max-h-48 overflow-y-auto">
              {pluginRecent.map((r) => (
                <li key={r.reportId} className="flex justify-between gap-2">
                  <span className="truncate">
                    {r.brand} · {r.failureStage}
                  </span>
                  <span className="text-slate-400 shrink-0">{fmtTime(r.createdAt)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-4">
        <h4 className="text-sm font-semibold text-slate-700">
          Webpage/app failures ({clientSummary?.date ?? "—"})
        </h4>
        <MetricCard
          label="Today"
          value={clientSummary?.totalToday ?? 0}
          warn={(clientSummary?.totalToday ?? 0) > 0}
        />
        {clientSummary && <BreakdownList byKey={clientSummary.byErrorClass} />}
        {clientRecent.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <h5 className="text-xs font-medium uppercase text-slate-500 mb-2">Recent</h5>
            <ul className="text-xs text-slate-600 space-y-1 max-h-48 overflow-y-auto">
              {clientRecent.map((r) => (
                <li key={r.reportId} className="flex justify-between gap-2">
                  <span className="truncate">
                    {r.app} · {r.errorClass} · {r.page}
                  </span>
                  <span className="text-slate-400 shrink-0">{fmtTime(r.createdAt)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
